/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {DataSplitMethod} from '../util/interfaces';
import {ModelResponse, ResponseData} from "@dvrd/dvr-controls";
import IDate from '@dvrd/idate';
import {getSubscriptionStats} from "../util/requestSender";

interface Props extends BaseProps {
    period_label?: string;
    active?: number;
    free?: number;
    trial?: number;
    inactive?: number;
    canceled?: number;
    expired?: number;
    unpaid?: number;
    failure?: number;
    past_due?: number;
}

interface Fields extends BaseFields {
    period_label: string;
    active: number;
    free: number;
    trial: number;
    inactive: number;
    canceled: number;
    expired: number;
    unpaid: number;
    failure: number;
    past_due: number;
    active_gain: number;
    free_gain: number;
    inactive_gain: number;
    canceled_gain: number;
    expired_gain: number;
    trial_gain: number;
    active_gain_perc: number;
    free_gain_perc: number;
    inactive_gain_perc: number;
    canceled_gain_perc: number;
    expired_gain_perc: number;
    trial_gain_perc: number;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    period_label: '',
    active: 0,
    free: 0,
    trial: 0,
    inactive: 0,
    expired: 0,
    canceled: 0,
    unpaid: 0,
    failure: 0,
    past_due: 0,
    active_gain: 0,
    free_gain: 0,
    canceled_gain: 0,
    expired_gain: 0,
    trial_gain: 0,
    inactive_gain: 0,
    active_gain_perc: 0,
    free_gain_perc: 0,
    inactive_gain_perc: 0,
    canceled_gain_perc: 0,
    expired_gain_perc: 0,
    trial_gain_perc: 0,
};

const fieldsDefinition: { [key in keyof Fields]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    period_label: createFieldDef(ModelFieldType.STRING),
    active: createFieldDef(ModelFieldType.NUMBER),
    free: createFieldDef(ModelFieldType.NUMBER),
    trial: createFieldDef(ModelFieldType.NUMBER),
    inactive: createFieldDef(ModelFieldType.NUMBER),
    expired: createFieldDef(ModelFieldType.NUMBER),
    canceled: createFieldDef(ModelFieldType.NUMBER),
    unpaid: createFieldDef(ModelFieldType.NUMBER),
    failure: createFieldDef(ModelFieldType.NUMBER),
    past_due: createFieldDef(ModelFieldType.NUMBER),
    trial_gain: createFieldDef(ModelFieldType.NUMBER),
    canceled_gain: createFieldDef(ModelFieldType.NUMBER),
    expired_gain: createFieldDef(ModelFieldType.NUMBER),
    free_gain: createFieldDef(ModelFieldType.NUMBER),
    active_gain: createFieldDef(ModelFieldType.NUMBER),
    inactive_gain: createFieldDef(ModelFieldType.NUMBER),
    active_gain_perc: createFieldDef(ModelFieldType.NUMBER),
    free_gain_perc: createFieldDef(ModelFieldType.NUMBER),
    inactive_gain_perc: createFieldDef(ModelFieldType.NUMBER),
    canceled_gain_perc: createFieldDef(ModelFieldType.NUMBER),
    expired_gain_perc: createFieldDef(ModelFieldType.NUMBER),
    trial_gain_perc: createFieldDef(ModelFieldType.NUMBER),
};

const ignoreProps: Array<keyof Props> = [];

export default class SubscriptionStats extends BaseModel<Props, Fields> {
    static getAll = (page: number, limit: number, splitMethod: DataSplitMethod, periodStart: IDate | undefined,
                     periodEnd: IDate | undefined, callback: ModelResponse<SubscriptionStats[]>) => {
        getSubscriptionStats({
            data: {
                to_date: periodEnd?.format('YYYY-MM-DD'), from_date: periodStart?.format('YYYY-MM-DD'),
                limit, page, split_method: splitMethod,
            }, callback: (data: ResponseData) => {
                const stats: SubscriptionStats[] = data.success ? data.stats.map((stat: any) => new SubscriptionStats(stat)) : [];
                callback(stats, data.success, data);
            }
        })
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get periodLabel(): string {
        return this.fields.period_label;
    }

    set periodLabel(period_label: string) {
        this.fields.period_label = period_label;
    }

    get active(): number {
        return this.fields.active;
    }

    get combinedActive(): number {
        return this.active + this.unpaid + this.pastDue;
    }

    set active(active: number) {
        this.fields.active = active;
    }

    get free(): number {
        return this.fields.free;
    }

    set free(free: number) {
        this.fields.free = free;
    }

    get trial(): number {
        return this.fields.trial;
    }

    set trial(trial: number) {
        this.fields.trial = trial;
    }

    get inactive(): number {
        return this.fields.inactive;
    }

    get combinedInactive(): number {
        return this.inactive + this.failure;
    }

    set inactive(inactive: number) {
        this.fields.inactive = inactive;
    }

    get canceled(): number {
        return this.fields.canceled;
    }

    get expired(): number {
        return this.fields.expired;
    }

    get unpaid(): number {
        return this.fields.unpaid;
    }

    get pastDue(): number {
        return this.fields.past_due;
    }

    set unpaid(unpaid: number) {
        this.fields.unpaid = unpaid;
    }

    get failure(): number {
        return this.fields.failure;
    }

    set failure(failure: number) {
        this.fields.failure = failure;
    }

    get activeGain(): number {
        return this.fields.active_gain;
    }

    get activeGainPerc(): number {
        return this.fields.active_gain_perc;
    }

    get freeGain(): number {
        return this.fields.free_gain;
    }

    get freeGainPerc(): number {
        return this.fields.free_gain_perc;
    }

    get inactiveGain(): number {
        return this.fields.inactive_gain;
    }

    get inactiveGainPerc(): number {
        return this.fields.inactive_gain_perc;
    }

    get trialGain(): number {
        return this.fields.trial_gain;
    }

    get trialGainPerc(): number {
        return this.fields.trial_gain_perc;
    }

    get canceledGain(): number {
        return this.fields.canceled_gain;
    }

    get expiredGain(): number {
        return this.fields.expired_gain;
    }

    get simpleStats() {
        return {
            active: this.combinedActive,
            free: this.free,
            trial: this.trial,
            inactive: this.combinedInactive,
            canceled: this.canceled,
            expired: this.expired,
        };
    }

    calculateGain = (comparingStat: SubscriptionStats) => {
        this.fields.active_gain = this.combinedActive - comparingStat.combinedActive;
        this.fields.active_gain_perc = this.fields.active_gain / comparingStat.combinedActive * 100;
        this.fields.free_gain = this.free - comparingStat.free;
        this.fields.free_gain_perc = this.fields.free_gain / comparingStat.free * 100;
        this.fields.trial_gain = this.trial - comparingStat.trial;
        this.fields.trial_gain_perc = this.fields.trial_gain / comparingStat.trial * 100;
        this.fields.canceled_gain = this.fields.canceled - comparingStat.canceled;
        this.fields.canceled_gain_perc = this.fields.canceled_gain / comparingStat.canceled * 100;
        this.fields.expired_gain = this.fields.expired - comparingStat.expired;
        this.fields.expired_gain_perc = this.fields.expired_gain / comparingStat.expired * 100;
        this.fields.inactive_gain = this.combinedInactive - comparingStat.combinedInactive;
        this.fields.inactive_gain_perc = this.fields.inactive_gain / comparingStat.combinedInactive * 100;
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
        }
    };
}